import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchIdeas } from '../api/ideas';
import { FaSearch, FaStar } from 'react-icons/fa';

function Home({ token }) {
  const [ideas, setIdeas] = useState([]);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Filter ideas based on search term
  const filteredIdeas = ideas.filter(idea =>
    idea.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (idea.description && idea.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (idea.tags && idea.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())))
  );

  // Separate pinned and unpinned ideas
  const pinnedIdeas = filteredIdeas.filter(idea => idea.is_pinned);
  const unpinnedIdeas = filteredIdeas.filter(idea => !idea.is_pinned);

  useEffect(() => {
    fetchAllIdeas();
  }, [token]);

  const fetchAllIdeas = async () => {
    try {
      const response = await fetchIdeas(token);
      setIdeas(Array.isArray(response) ? response : []);
      setError('');
    } catch (err) {
      console.error('Failed to fetch ideas:', err);
      setError('Failed to fetch ideas: ' + (err.response?.data?.detail || err.message));
    }
  };

  return (
    <div className="flex flex-col min-h-full bg-[var(--bg-primary)]">
      
      {/* Welcome Text */}
      <div className="text-center py-16 px-4">
        <h1 className="text-4xl md:text-5xl font-bold text-[var(--text-primary)] animate-fade-in-up">
          Welcome to Your Idea Hub
        </h1>
      </div>

      {/* Header with Search and New Idea Button */}
      <div className="py-8">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-4">
            {/* Search Bar */}
            <div className="relative max-w-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaSearch className="text-[var(--text-secondary)]" />
              </div>
              <input
                type="text"
                className="w-full pl-10 pr-4 py-3 border border-[var(--border-color)] rounded-lg bg-[var(--bg-secondary)] text-[var(--text-primary)] placeholder-[var(--text-secondary)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:border-transparent transition-all duration-200 hover:shadow-md"
                placeholder="Search your ideas..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            {/* New Idea Button */}
            <Link
              to="/create-idea"
              className="px-6 py-3 bg-[var(--primary)] text-white rounded-lg font-medium hover:opacity-90 transition-all duration-200 whitespace-nowrap transform hover:scale-105 active:scale-95 hover:shadow-lg button-magical"
            >
              New Idea
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto w-full px-4 pb-8">
        {error && (
          <div className="bg-danger-light border text-danger px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Pinned Ideas Section */}
        {pinnedIdeas.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-6">
              Pinned Ideas
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {pinnedIdeas.map((idea) => (
                <Link
                  key={idea.id}
                  to={`/idea/${idea.id}`}
                  className="relative h-48 bg-[var(--bg-secondary)] p-6 pt-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-[var(--border-color)] transform hover:scale-102 hover:-translate-y-1 group"
                >
                  <div className="flex items-center space-x-3 mb-2">
                    <span className="text-2xl transition-transform duration-200 group-hover:scale-110">{idea.icon || '😊'}</span>
                    <h3 className="text-lg font-semibold text-[var(--text-primary)]">{idea.title}</h3>
                  </div>
                  <p className="text-sm text-[var(--text-secondary)] line-clamp-3">{idea.description || 'No description'}</p>
                  <p className="text-xs text-[var(--text-secondary)] mt-2">Status: {idea.status}</p>
                  <p className="text-xs text-[var(--text-secondary)]">
                    Last Activity: {new Date(idea.created_at).toLocaleString()}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* All Ideas Section */}
        <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-6">
          {searchTerm ? `Search Results (${filteredIdeas.length})` : 'Your Ideas'}
        </h2>
        {filteredIdeas.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredIdeas.map((idea, index) => (
              <Link
                key={idea.id}
                to={`/idea/${idea.id}`}
                className="relative h-48 bg-[var(--bg-secondary)] p-6 pt-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-[var(--border-color)] transform hover:scale-102 hover:-translate-y-1 group"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="flex items-center space-x-3 mb-2">
                  <span className="text-2xl transition-transform duration-200 group-hover:scale-110">{idea.icon || '😊'}</span>
                  <h3 className="text-lg font-semibold text-[var(--text-primary)]">{idea.title}</h3>
                </div>
                <p className="text-sm text-[var(--text-secondary)] line-clamp-3">{idea.description || 'No description'}</p>
                <p className="text-xs text-[var(--text-secondary)] mt-2">Status: {idea.status}</p>
                <p className="text-xs text-[var(--text-secondary)]">
                  Last Activity: {new Date(idea.created_at).toLocaleString()}
                </p>
              </Link>
            ))}
          </div>
        ) : (
          <p className="text-[var(--text-secondary)] text-center">
            {searchTerm ? 'No ideas found matching your search.' : 'No ideas found. Create one to get started!'}
          </p>
        )}
      </div>
    </div>
  );
}

export default Home;